import axios from 'axios';
import { baseURL } from '../Constants';
import { MapWithAreaStatsDto, MapGeometryDto, MatchAreaSummaryDto, GameAreaSummaryDto, PlayerAreaProfileDto } from '../models/PlayerArea';

const url = `${baseURL}/PlayerArea`;

// Get map with match area statistics - primary endpoint for MatchPage
export const getMapWithMatchStats = async (
    mapName: string,
    matchId: string
): Promise<MapWithAreaStatsDto | null> => {
    try {
        const response = await axios.get(`${url}/maps/${mapName}/match/${matchId}`);
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 404) {
            return null;
        }
        console.error('Error fetching map with match stats:', error);
        throw error;
    }
};

// Get match area summary (all games in a match)
export const getMatchAreaSummary = async (
    matchId: string
): Promise<MatchAreaSummaryDto | null> => {
    try {
        const response = await axios.get(`${url}/match/${matchId}`);
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 404) {
            return null;
        }
        console.error('Error fetching match area summary:', error);
        throw error;
    }
};

// Get game area summary
export const getGameAreaSummary = async (
    gameId: string
): Promise<GameAreaSummaryDto | null> => {
    try {
        const response = await axios.get(`${url}/game/${gameId}`);
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 404) {
            return null;
        }
        console.error('Error fetching game area summary:', error);
        throw error;
    }
};

// Get map geometry separately (for caching)
export const getMapGeometry = async (
    mapName: string
): Promise<MapGeometryDto | null> => {
    try {
        const response = await axios.get(`${url}/maps/${mapName}/geometry`);
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 404) {
            return null;
        }
        console.error('Error fetching map geometry:', error);
        throw error;
    }
};

// Get available maps list
export const getAvailableMaps = async (): Promise<string[]> => {
    try {
        const response = await axios.get(`${url}/maps`);
        return response.data;
    } catch (error) {
        console.error('Error fetching available maps:', error);
        return [];
    }
};

// Get raw map JSON
export const getMapJson = async (mapName: string): Promise<any | null> => {
    try {
        const response = await axios.get(`${url}/maps/${mapName}/json`);
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 404) {
            return null;
        }
        console.error('Error fetching map JSON:', error);
        throw error;
    }
};

// Get map with player area statistics - for player profile page
export const getMapWithPlayerStats = async (
    mapName: string,
    steamId: string,
    season?: number,
    side?: string
): Promise<MapWithAreaStatsDto | null> => {
    try {
        const params = new URLSearchParams();
        if (season) params.append('season', season.toString());
        if (side) params.append('side', side.toUpperCase());

        const queryString = params.toString();
        const response = await axios.get(
            `${url}/maps/${mapName}/player/${steamId}${queryString ? `?${queryString}` : ''}`
        );
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 404) {
            return null;
        }
        console.error('Error fetching map with player stats:', error);
        throw error;
    }
};

// Get seasons that have position data for a player
export const getSeasonsWithPositionData = async (
    steamId: string
): Promise<number[]> => {
    try {
        const response = await axios.get(`${url}/player/${steamId}/seasons`);
        return response.data;
    } catch (error) {
        console.error('Error fetching seasons with position data:', error);
        return [];
    }
};

// Get player area profile (aggregated stats)
export const getPlayerAreaProfile = async (
    steamId: string,
    season?: number,
    mapName?: string,
    side?: string
): Promise<PlayerAreaProfileDto | null> => {
    try {
        const params = new URLSearchParams();
        if (season) params.append('season', season.toString());
        if (mapName) params.append('mapName', mapName);
        if (side) params.append('side', side.toUpperCase());

        const queryString = params.toString();
        const response = await axios.get(
            `${url}/player/${steamId}${queryString ? `?${queryString}` : ''}`
        );
        return response.data;
    } catch (error: any) {
        if (error.response?.status === 404) {
            return null;
        }
        console.error('Error fetching player area profile:', error);
        throw error;
    }
};
